/**
 * lessonRecapModel — pure helpers for the family-facing Lesson Recap surface.
 *
 * A Lesson Recap is per (lesson × visibility tier): conversational rich text a
 * teacher writes after a lesson. Three tiers are written, and the tier IS the
 * wall (core.md → Note vocabulary):
 *
 *   student — student + parent + teacher
 *   parent  — parent + teacher   ("what I'd tell you but not them")
 *   teacher — teacher only       NEVER leaves the studio
 *
 * THE `teacher` TIER IS THE WHOLE POINT OF THIS FILE EXISTING SEPARATELY FROM
 * THE COMPONENT. It is excluded in three independent places — the Firestore
 * rule (documents, customer branch), the query in LessonRecapService, and
 * familyVisibleRecaps() below. That is the same four-layer shape the Song Notes
 * surface uses, and it is not redundancy theatre: the third layer is the one
 * still standing if the two above it ever fail open. Do not "simplify" it away.
 *
 * WHY NOT ENFORCE student-vs-parent HERE: today a claimed account is ONE
 * guardian principal per family, so both tiers are legitimately that reader's.
 * Splitting them needs a real student principal (P2b), not a UI predicate —
 * faking it here would be exactly the "UI filter pretending to be a wall"
 * pattern core.md forbids. When P2b lands this surface gets NARROWER, via the
 * rule, not rewritten.
 */

export const RECAP_VISIBILITY = {
	STUDENT: 'student',
	PARENT: 'parent',
	TEACHER: 'teacher',
};

/**
 * The tiers a FAMILY principal may read. The client query must constrain
 * `where('visibility','in', FAMILY_VISIBILITIES)` — the rule is a predicate,
 * not a filter, so an unconstrained list query is DENIED WHOLESALE rather than
 * silently trimmed. Same trap the Song Notes surface documents.
 */
export const FAMILY_VISIBILITIES = [RECAP_VISIBILITY.STUDENT, RECAP_VISIBILITY.PARENT];

export const isFamilyVisible = (visibility) => FAMILY_VISIBILITIES.includes(visibility);

/**
 * The tiers the SIGNED-IN principal may read, which is what the query must ask
 * for. A STUDENT principal (P2b) is admitted by the rule to the `student` tier
 * only, about themselves; asking it for `['student','parent']` is not trimmed to
 * the readable half, it is DENIED WHOLESALE, and the page reads "we couldn't
 * load lesson recaps" to a student who has recaps. Found 2026-09-22 building
 * pass 2 batch C: the Past lessons tab had been doing exactly that since P2b.
 */
export const visibilitiesFor = ({ studentOnly = false } = {}) =>
	studentOnly ? [RECAP_VISIBILITY.STUDENT] : FAMILY_VISIBILITIES;

/**
 * Display label for a tier, from the FAMILY's point of view.
 *
 * The `parent` tier is labelled rather than rendered bare: a parent seeing two
 * blocks under one lesson with no explanation reads it as a duplicate. Naming
 * it also sets the right expectation — this is the note written for them, and
 * their child is not reading it.
 */
export const TIER_LABEL = {
	[RECAP_VISIBILITY.STUDENT]: 'Lesson recap',
	// "For parents", the pass 2 design's wording (2026-09-21): shorter, and it
	// names the reader rather than the kind of text.
	[RECAP_VISIBILITY.PARENT]: 'For parents',
};

/** A recap doc carries content only if its TipTap JSON has at least one non-empty text node. */
export const hasContent = (recap) => {
	if (!recap) return false;
	if (typeof recap.plainText === 'string' && recap.plainText.trim()) return true;
	// plainText is derived on save; fall back to walking the JSON so a doc saved
	// by an older path (or with plainText unset) still renders rather than
	// silently reading as empty.
	const walk = (node) => {
		if (!node || typeof node !== 'object') return false;
		if (typeof node.text === 'string' && node.text.trim()) return true;
		return (node.content || []).some(walk);
	};
	return walk(recap.content);
};

const toMillis = (value) => {
	if (!value) return 0;
	if (typeof value.toMillis === 'function') return value.toMillis();
	if (typeof value.seconds === 'number') return value.seconds * 1000;
	const parsed = new Date(value).getTime();
	return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * The LESSON's date, read off a session-record lessonId (`occ_<slotId>_<YYYY-MM-DD>`,
 * lessonService.lessonIdForOccurrence). Preferred over createdAt, which is when
 * the recap was WRITTEN: a recap finished the next morning would otherwise be
 * dated the day after the lesson it describes. Local-date constructor, never
 * `new Date('YYYY-MM-DD')` (UTC midnight = the evening before in Central).
 * Null for any other id shape (the legacy MMS lesson ids), which then fall back.
 */
export const lessonDateFromId = (lessonId) => {
	const m = /^occ_.+_(\d{4})-(\d{2})-(\d{2})$/.exec(lessonId || '');
	return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

export const recapDate = (recap) => {
	const ms = toMillis(recap?.createdAt) || toMillis(recap?.updatedAt);
	return ms ? new Date(ms) : null;
};

/**
 * Group family-visible recaps into one entry per LESSON, newest first.
 *
 * Grouped by lessonId because the two tiers of one lesson are two documents —
 * a visibility boundary is a document boundary (core.md), so the split exists
 * on disk and has to be re-joined for display. A recap with no lessonId still
 * gets its own group keyed by doc id rather than being dropped: losing a
 * teacher's note because a field is missing is worse than an odd grouping.
 *
 * Empty-content recaps are dropped. All three tiers are CREATED when a teacher
 * opens the recap editor, so an untouched tier exists as a blank document —
 * rendering it would show a family an empty box implying nothing was said.
 */
export const groupRecapsByLesson = (recaps = []) => {
	const visible = familyVisibleRecaps(recaps).filter(hasContent);
	const groups = new Map();

	visible.forEach((recap) => {
		const key = recap.lessonId || `doc:${recap.id}`;
		if (!groups.has(key)) {
			groups.set(key, { lessonId: recap.lessonId || null, key, tiers: [], sort: 0 });
		}
		const group = groups.get(key);
		group.tiers.push(recap);
		group.sort = Math.max(group.sort, toMillis(recap.createdAt) || toMillis(recap.updatedAt));
	});

	return [...groups.values()]
		.map((group) => ({
			...group,
			date: lessonDateFromId(group.lessonId) || (group.sort ? new Date(group.sort) : null),
			// Stamped on new recaps since 2026-09-22; older ones carry none and the
			// surface simply omits "with …" rather than guessing a teacher.
			authorFirstName: (group.tiers.find((t) => t.authorFirstName) || {}).authorFirstName || null,
			// Student tier first: it is the note the family reads together, and
			// the parent-only note is the aside beneath it.
			tiers: group.tiers.sort((a, b) => {
				if (a.visibility === b.visibility) return 0;
				return a.visibility === RECAP_VISIBILITY.STUDENT ? -1 : 1;
			}),
		}))
		.sort((a, b) => b.sort - a.sort);
};

/**
 * THE THIRD ASSERTION OF THE TEACHER-TIER WALL. Never remove in the belief that
 * the rule and the query already cover it — that is precisely the reasoning
 * that turns three independent layers into one.
 */
export const familyVisibleRecaps = (recaps = []) =>
	recaps.filter((r) => r && isFamilyVisible(r.visibility));

const lessonRecapModel = {
	RECAP_VISIBILITY,
	visibilitiesFor,
	lessonDateFromId,
	FAMILY_VISIBILITIES,
	TIER_LABEL,
	isFamilyVisible,
	familyVisibleRecaps,
	groupRecapsByLesson,
	hasContent,
	recapDate,
};

export default lessonRecapModel;
